/**
 * Google Analytics 4 mirror of the Studio signup events.
 *
 * posthog-tracker.ts owns consent and decides when analytics is active. This
 * module only loads gtag.js after consent, forwards the signup events, and
 * removes the GA cookies when consent is withdrawn. Page views stay in the
 * marketing site, where visits land; GA4 joins the two through the `_ga`
 * cookie shared on `.spatius.ai`.
 *
 * A visitor who had not decided on consent on the marketing site or the docs
 * was never seen by GA4 there. Those sites hand the visit's source over in
 * `sw_landing_*` query parameters, and the first event reports it as the
 * campaign, so the signup is not counted as Direct.
 */
import { expireSharedCookie } from './analytics-consent'
import { sanitizeAnalyticsPath } from './posthog-privacy'

type Gtag = (...args: unknown[]) => void
type Ga4Window = Window & { dataLayer?: unknown[], gtag?: Gtag } & Record<string, unknown>

const GTAG_SCRIPT_URL = 'https://www.googletagmanager.com/gtag/js'
const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{4,20}$/
const OWNED_ROOT_DOMAIN = 'spatius.ai'
const LANDING_QUERY_KEYS = {
  sw_landing_source: 'utm_source',
  sw_landing_medium: 'utm_medium',
  sw_landing_campaign: 'utm_campaign',
} as const
const LANDING_VALUE_PATTERN = /^[\w.-][\w .:/-]{0,99}$/
// Referrers that only continue a visit: our own sites and Stripe's payment
// pages. Sign-in returns are recognised by their callback parameters instead,
// since github.com also links to Studio from READMEs.
const CONTINUATION_REFERRER_DOMAINS = ['spatius.ai', 'spatialwalk.cn', 'stripe.com']
const SIGN_IN_CALLBACK_QUERY_KEYS = ['code', 'state', 'error']

const GA4_EVENTS: Record<string, string> = {
  signup_started: 'sign_up_start',
  signup_succeeded: 'sign_up_complete',
}

let loadedMeasurementId = ''
// Read before the router and the login page rewrite the address bar.
const isSignInReturn = canUseDOM() && SIGN_IN_CALLBACK_QUERY_KEYS.some(key => new URLSearchParams(window.location.search).has(key))
const landingCampaign = takeLandingCampaign()
let landingCampaignReported = false

export function resolveGa4MeasurementId(value: unknown): string {
  const measurementId = typeof value === 'string' ? value.trim().toUpperCase() : ''
  return MEASUREMENT_ID_PATTERN.test(measurementId) ? measurementId : ''
}

export function enableGa4(measurementId: string): void {
  // The GA4 stream covers spatius.ai only; other deployments never report to it.
  if (!measurementId || !canUseDOM() || !isOwnedHost(window.location.hostname.toLowerCase()))
    return
  const ga4Window = window as unknown as Ga4Window
  ga4Window[`ga-disable-${measurementId}`] = false
  if (loadedMeasurementId === measurementId) {
    gtag('consent', 'update', { analytics_storage: 'granted' })
    return
  }

  ga4Window.dataLayer = ga4Window.dataLayer || []
  ga4Window.gtag = function gtag() {
    // gtag.js consumes the Arguments object itself, not a copied array.
    // eslint-disable-next-line prefer-rest-params
    ga4Window.dataLayer?.push(arguments)
  }
  // The banner grants analytics only; advertising storage and signals stay off.
  gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
  gtag('js', new Date())
  gtag('config', measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    // Arriving from our own sites or back from sign-in and payment pages must
    // not replace the source of the visit that led here. Any value of
    // ignore_referrer turns it on, so the key is omitted for other referrers.
    ...(isSignInReturn || isContinuationReferrer(document.referrer) ? { ignore_referrer: 'true' } : {}),
  })

  const script = document.createElement('script')
  script.async = true
  script.src = `${GTAG_SCRIPT_URL}?id=${encodeURIComponent(measurementId)}`
  document.head.appendChild(script)
  loadedMeasurementId = measurementId
}

export function disableGa4(measurementId: string): void {
  if (!measurementId || !canUseDOM())
    return
  const ga4Window = window as unknown as Ga4Window
  ga4Window[`ga-disable-${measurementId}`] = true
  if (loadedMeasurementId)
    gtag('consent', 'update', { analytics_storage: 'denied' })
  expireSharedCookie('_ga')
  expireSharedCookie(`_ga_${measurementId.slice(2)}`)
}

/** Forwards the PostHog events that GA4 tracks; others are ignored. */
export function trackGa4Event(measurementId: string, eventName: string, properties: Record<string, unknown>): void {
  if (!isGa4Active(measurementId))
    return
  const ga4EventName = GA4_EVENTS[eventName]
  if (!ga4EventName)
    return
  // Query strings can carry OAuth codes and state; GA4 gets the path only,
  // plus the handed-over campaign on the first event.
  const campaignQuery = landingCampaign && !landingCampaignReported ? `?${landingCampaign}` : ''
  landingCampaignReported = true
  gtag('event', ga4EventName, {
    page_location: `${window.location.origin}${sanitizeAnalyticsPath(window.location.pathname)}${campaignQuery}`,
    ...(typeof properties.method === 'string' ? { method: properties.method } : {}),
  })
}

/** Reads the handed-over landing source once and removes it from the address bar. */
function takeLandingCampaign(): string {
  if (!canUseDOM() || !window.location.search)
    return ''
  let url: URL
  try {
    url = new URL(window.location.href)
  }
  catch {
    return ''
  }
  const campaign = new URLSearchParams()
  for (const [key, utmKey] of Object.entries(LANDING_QUERY_KEYS)) {
    const value = url.searchParams.get(key)?.trim()
    if (value && LANDING_VALUE_PATTERN.test(value))
      campaign.set(utmKey, value)
    url.searchParams.delete(key)
  }
  if (url.toString() !== window.location.href) {
    try {
      // A copied or bookmarked link must not credit the next visitor with this source.
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
    }
    catch {
      // Analytics must never break the product flow.
    }
  }
  return campaign.has('utm_source') && campaign.has('utm_medium') ? campaign.toString() : ''
}

function isGa4Active(measurementId: string): boolean {
  if (!measurementId || !canUseDOM() || loadedMeasurementId !== measurementId)
    return false
  return (window as unknown as Ga4Window)[`ga-disable-${measurementId}`] !== true
}

function gtag(...args: unknown[]): void {
  try {
    (window as unknown as Ga4Window).gtag?.(...args)
  }
  catch {
    // Analytics must never break the product flow.
  }
}

function isContinuationReferrer(referrer: string): boolean {
  let hostname = ''
  try {
    hostname = new URL(referrer).hostname.toLowerCase()
  }
  catch {
    return false
  }
  return CONTINUATION_REFERRER_DOMAINS.some(domain => hostname === domain || hostname.endsWith(`.${domain}`))
}

function isOwnedHost(hostname: string): boolean {
  return hostname === OWNED_ROOT_DOMAIN || hostname.endsWith(`.${OWNED_ROOT_DOMAIN}`)
}

function canUseDOM(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}
